import { toast } from 'react-toastify';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FarmshareNavigationTabs } from './farmshare/FarmshareNavigationTabs';
import { IconButton } from './buttons/IconButton';
import { ChevronLeft, Plus, Minus, Phone, CalendarCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { getEquipmentTranslation } from '../utils/equipmentTranslation';
import i18n from '../i18n';

const DetailEquipment = ({ equip, contact, showResModal, setQuantity, quantity }) => {
  const [showContact, setShowContact] = useState(false);
  const [contactHeight, setContactHeight] = useState(0);
  const contactRef = useRef(null);
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language;

  useEffect(() => {
    if (contactRef.current) {
      setContactHeight(showContact ? contactRef.current.scrollHeight : 0);
    }
  }, [showContact]);

  if (!equip || !equip._id) {
    return <p className="text-center p-6 text-red-500">Equipment not found or still loading.</p>;
  }

  const contactInfo = contact || { firstName: '', lastName: '', email: '' };
  const equipmentName = getEquipmentTranslation(equip, 'name', currentLang);
  const equipmentDescription = getEquipmentTranslation(equip, 'description', currentLang);

  const handleRentEquipment = () => {
    console.log('Equipment object:', equip);
    if (!equip._id) {
      toast.error(i18n.t('equipment.idMissing'));
      return;
    }

    showResModal(true);
  };

  const toggleContactInfo = () => {
    setShowContact(!showContact);
  };

  const actionButtonClasses =
    'flex items-center justify-center gap-2 rounded-full border-2 border-SLIGreen px-8 py-3 font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-SLIGreen focus:ring-offset-2';

  return (
    <section className="page-responsive-x-padding overflow-hidden bg-white pb-16">
      <FarmshareNavigationTabs />
      <IconButton
        LeftIcon={<ChevronLeft className="mr-3 h-5 w-5" />}
        label={t('equipment.detail.backToEquipment')}
        onClick={() => {
          navigate(ROUTES.HOME);
        }}
        className="mb-12 items-center text-lg font-semibold text-SLIBlack transition-colors duration-200 hover:text-SLIGreen"
      />

      <div className="grid gap-12 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] xl:gap-16">
        <div className="flex w-full justify-center lg:justify-start">
          <div className="relative w-full max-w-[520px] overflow-hidden rounded-[32px] border border-gray-200 shadow-sm">
            <img src={equip.images[0]} alt={equipmentName} className="h-full w-full object-cover" />
          </div>
        </div>

        <div className="max-w-xl">
          <div className="pb-6">
            <h2 className="text-3xl font-semibold text-SLIBlack md:text-5xl">{equipmentName}</h2>
            <p className="mt-3 text-lg font-semibold text-SLIGreen">{equip.location}</p>
            <p className="mt-4 text-2xl font-semibold text-SLIBlack">${equip.rentalPrice}/Day</p>
            <p className="mt-6 text-base leading-relaxed text-gray-600 md:text-lg">
              {equipmentDescription}
            </p>
          </div>

          <div className="inline-flex items-center rounded-full border-2 border-SLIGreen bg-white px-3 py-2 text-SLIBlack">
            <button
              className="flex h-10 w-10 items-center justify-center rounded-full text-SLIGreen transition-colors duration-150 hover:bg-SLIGreen hover:text-white"
              aria-label="Decrease quantity"
              onClick={() => {
                setQuantity((state) => (state > 1 ? state - 1 : state));
              }}
            >
              <Minus className="h-5 w-5" />
            </button>
            <span className="mx-6 min-w-[1.5rem] text-center text-lg font-semibold">
              {quantity}
            </span>
            <button
              className="flex h-10 w-10 items-center justify-center rounded-full text-SLIGreen transition-colors duration-150 hover:bg-SLIGreen hover:text-white"
              aria-label="Increase quantity"
              onClick={() => {
                const maxQty =
                  equip.availableInstances !== undefined
                    ? equip.availableInstances
                    : equip.qty || 0;
                setQuantity((state) => (maxQty > state ? state + 1 : state));
              }}
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>

          <div
            className="mt-6 overflow-hidden rounded-2xl border border-transparent transition-all duration-300 ease-in-out"
            style={{
              maxHeight: contactHeight,
              opacity: showContact ? 1 : 0,
              borderColor: showContact ? '#A1E8B4' : 'transparent',
              backgroundColor: showContact ? '#F4FFF6' : 'transparent',
            }}
          >
            <div ref={contactRef} className="p-6">
              <h3 className="mb-3 text-lg font-semibold text-SLIGreen">
                {t('equipment.detail.contactInformation')}
              </h3>
              <p className="text-base text-SLIBlack">
                <span className="font-medium">{t('equipment.detail.name')}:</span>{' '}
                {contactInfo?.firstName || 'N/A'} {contactInfo?.lastName || ''}
              </p>
              <p className="mt-2 text-base text-SLIBlack">
                <span className="font-medium">{t('equipment.detail.email')}:</span>{' '}
                {contactInfo?.email || t('equipment.detail.noEmailProvided')}
              </p>
              {contactInfo.email ? (
                <p className="mt-4 text-sm italic text-SLIGreen">
                  {t('equipment.detail.contactMessage')}
                </p>
              ) : (
                <p className="mt-4 text-sm italic text-SLIGreen">
                  {t('equipment.detail.noContactMessage')}
                </p>
              )}
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <button
              className={`${actionButtonClasses} text-SLIGreen hover:bg-SLIGreen hover:text-white`}
              onClick={toggleContactInfo}
            >
              <Phone className="h-5 w-5" />
              {showContact
                ? t('equipment.detail.hideContactInfo')
                : t('equipment.detail.contactOwner')}
            </button>
            <button
              className={`${actionButtonClasses} text-SLIGreen hover:bg-SLIGreen hover:text-white`}
              onClick={handleRentEquipment}
            >
              <CalendarCheck className="h-5 w-5" />
              {t('equipment.detail.makeReservation')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export { DetailEquipment };
